import { type ComponentProps, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ArchiveIcon, UndoIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { MeetingTargetType } from '@treams/treams-network';
import { Button, Popover } from '@treams/treams-ui';

import MeetingSeriesConfirmationModal from '../meetings/MeetingSeriesConfirmationModal';
import useMeetingMutations from '../meetings/hooks/useMeetingMutations';

export default function ArchiveRestoreButton(
	props: { note: graphql.NoteFragment } & Omit<
		ComponentProps<typeof Button>,
		'title' | 'type' | 'icon' | 'onClick'
	>,
) {
	const { t } = useTranslation();

	const [archive] = graphql.useArchiveNoteMutation();
	const [restore] = graphql.useRestoreNoteMutation();

	const { isArchived, id: noteID, subject, meeting } = props.note ?? {};

	const title = useMemo(() => {
		if (subject) {
			return isArchived ? t('oneOnOne.show.toolbar.restore') : t('oneOnOne.show.toolbar.archive');
		} else {
			return isArchived ? t('note.show.toolbar.restore') : t('note.show.toolbar.archive');
		}
	}, [subject, isArchived, t]);

	const { cancel: cancelMeeting, restore: restoreMeeting } = useMeetingMutations({
		target: { type: MeetingTargetType.Note, id: noteID },
		hasMeeting: !!meeting,
	});
	const [confirmCancelSeries, setConfirmCancelSeries] = useState<boolean>(false);

	const onCancelMeetingAndArchive = useCallback(
		async (cancelSeries?: boolean) => {
			if (meeting && !meeting.isCancelled) {
				await cancelMeeting({ cancelSeries });
			}
			await archive({ variables: { input: { noteID } } });
		},
		[meeting, noteID, cancelMeeting, archive],
	);

	const onClick = async () => {
		if (isArchived) {
			if (meeting?.isCancelled) {
				await restoreMeeting();
			}
			await restore({ variables: { input: { noteID } } });
		} else {
			if (subject && meeting?.series && !meeting.isCancelled) {
				setConfirmCancelSeries(true);
				return;
			}

			await onCancelMeetingAndArchive();
		}
	};

	if (!props.note || !noteID) {
		return null;
	}

	return (
		<>
			<Popover
				backdrop
				center
				visible={confirmCancelSeries}
				onDismiss={() => setConfirmCancelSeries(false)}
			>
				<MeetingSeriesConfirmationModal.Cancel
					onClose={() => setConfirmCancelSeries(false)}
					onOnlyThisMeeting={() => onCancelMeetingAndArchive(false)}
					onAllFutureMeetings={() => onCancelMeetingAndArchive(true)}
				/>
			</Popover>
			<Button
				title={title}
				type="destructive"
				icon={isArchived ? <UndoIcon /> : <ArchiveIcon />}
				onClick={onClick}
				{...props}
			/>
		</>
	);
}
